import { FiBell } from "react-icons/fi";
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import toast from "react-hot-toast";
import {
  useGetNotificationsQuery,
  useGetUnreadCountQuery,
  useMarkAsReadMutation,
  useMarkAllAsReadMutation,
  useDeleteNotificationMutation,
  useBulkMarkAsReadMutation,
  useBulkDeleteNotificationsMutation,
} from "../../store/admin/notificationSlice";
import { ConfirmationModal, TablePagination } from "../index";
import NotificationCard from "./NotificationCard";
import TableSkeletonLoader from "../Common/TableSkeletonLoader";
import { NOTIFICATION_TYPES, NOTIFICATION_CATEGORIES } from "../../utils/notificationTypes";
import { useUser } from "../../hooks";
import { useCompanyCapabilities } from "../../hooks/useCompanyCapabilities";
import { resolveNotificationNavigationTarget } from "../../utils/notificationNavigation";
import "./notifications.css";

const TAB_OPTIONS = ["all", "unread", "read"];

const CATEGORY_FILTER_OPTIONS = [
  { value: "", label: "All categories" },
  ...[
    NOTIFICATION_TYPES.INVESTMENT,
    NOTIFICATION_TYPES.MARKETPLACE,
    NOTIFICATION_TYPES.REAL_ESTATE,
    NOTIFICATION_TYPES.KYC,
    NOTIFICATION_TYPES.PAYMENT,
    NOTIFICATION_TYPES.SYSTEM,
    NOTIFICATION_TYPES.EVENT,
    NOTIFICATION_TYPES.EMINUTE,
    NOTIFICATION_TYPES.BIRTHDAY,
    NOTIFICATION_TYPES.MEMBERSHIP,
    NOTIFICATION_TYPES.GENERAL,
  ].map((value) => ({
    value,
    label: NOTIFICATION_CATEGORIES[value]?.name || value,
  })),
];

export default function NotificationsCenter({ guard = "staff" }) {
  const navigate = useNavigate();
  const user = useUser();
  const { capabilities } = useCompanyCapabilities();
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedNotifications, setSelectedNotifications] = useState([]);
  const [deleteConfirmModal, setDeleteConfirmModal] = useState({
    isOpen: false,
    notificationId: null,
  });
  const [bulkDeleteConfirmModal, setBulkDeleteConfirmModal] = useState({ isOpen: false });
  const [isDeleting, setIsDeleting] = useState(false);

  const activeTab = TAB_OPTIONS.includes(searchParams.get("tab"))
    ? searchParams.get("tab")
    : "all";
  const pageNumber = parseInt(searchParams.get("page"), 10) || 1;
  const typeFilter = searchParams.get("type") || "";

  const {
    data: notificationsData,
    isLoading,
    isFetching,
    refetch,
  } = useGetNotificationsQuery({
    page: pageNumber,
    status: activeTab,
    limit: 20,
    guard,
    type: typeFilter,
  });

  const { data: unreadCountData } = useGetUnreadCountQuery(guard);
  const [markAsRead] = useMarkAsReadMutation();
  const [markAllAsRead] = useMarkAllAsReadMutation();
  const [deleteNotification] = useDeleteNotificationMutation();
  const [bulkMarkAsRead] = useBulkMarkAsReadMutation();
  const [bulkDeleteNotifications] = useBulkDeleteNotificationsMutation();

  const notifications = notificationsData?.notifications || [];
  const pagination = notificationsData?.pagination || {};
  const meta = notificationsData?.meta || {};

  const unreadCount =
    meta.unread_count ??
    (typeof unreadCountData === "number"
      ? unreadCountData
      : unreadCountData?.unread_count ?? 0);

  const readCount =
    meta.read_count ??
    Math.max(0, (pagination.total || 0) - unreadCount);

  const totalCount = meta.total ?? pagination.total ?? notifications.length;

  const basePath =
    guard === "member"
      ? "/member/notifications"
      : guard === "investor"
        ? "/invest/notifications"
        : "/super_admin/notifications";

  const setTab = (tab) => {
    const params = new URLSearchParams(searchParams);
    if (tab !== "all") params.set("tab", tab);
    else params.delete("tab");
    params.delete("page");
    setSearchParams(params, { replace: true });
    setSelectedNotifications([]);
  };

  const setTypeFilter = (type) => {
    const params = new URLSearchParams(searchParams);
    if (type) params.set("type", type);
    else params.delete("type");
    params.delete("page");
    setSearchParams(params, { replace: true });
    setSelectedNotifications([]);
  };

  const handlePageChange = (newPage) => {
    const params = new URLSearchParams(searchParams);
    if (newPage > 1) params.set("page", String(newPage));
    else params.delete("page");
    setSearchParams(params, { replace: true });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  useEffect(() => {
    setSelectedNotifications([]);
  }, [activeTab, pageNumber]);

  const handleNotificationClick = async (notification) => {
    if (!notification.is_read) {
      await markAsRead({ notificationId: notification.id, guard });
    }
    const { path, usedFallback, reason } = resolveNotificationNavigationTarget({
      actionUrl: notification.action_url,
      guard,
      role: user?.role,
      capabilities,
      notificationId: notification.id,
      listPath: basePath,
    });

    if (usedFallback && reason === "permission_denied") {
      toast("This notification links to a page your role cannot access.", {
        icon: "ℹ️",
      });
    }

    navigate(path);
  };

  const handleMarkAllAsRead = async () => {
    await markAllAsRead(guard);
    refetch();
  };

  const handleBulkMarkAsRead = async () => {
    if (selectedNotifications.length === 0) return;
    await bulkMarkAsRead({ ids: selectedNotifications, guard });
    setSelectedNotifications([]);
    refetch();
  };

  const confirmDeleteNotification = async () => {
    if (!deleteConfirmModal.notificationId) return;
    setIsDeleting(true);
    try {
      await deleteNotification({
        notificationId: deleteConfirmModal.notificationId,
        guard,
      });
      setDeleteConfirmModal({ isOpen: false, notificationId: null });
      refetch();
    } finally {
      setIsDeleting(false);
    }
  };

  const confirmBulkDelete = async () => {
    if (selectedNotifications.length === 0) return;
    setIsDeleting(true);
    try {
      await bulkDeleteNotifications({ ids: selectedNotifications, guard });
      setSelectedNotifications([]);
      setBulkDeleteConfirmModal({ isOpen: false });
      refetch();
    } finally {
      setIsDeleting(false);
    }
  };

  const toggleSelect = (id) => {
    setSelectedNotifications((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const toggleSelectAllOnPage = () => {
    const pageIds = notifications.map((n) => n.id).filter(Boolean);
    const allSelected = pageIds.every((id) => selectedNotifications.includes(id));
    if (allSelected) {
      setSelectedNotifications((prev) => prev.filter((id) => !pageIds.includes(id)));
    } else {
      setSelectedNotifications((prev) => [...new Set([...prev, ...pageIds])]);
    }
  };

  const summaryCards = useMemo(
    () => [
      { key: "all", label: "All", value: totalCount, className: "notifications-summary-card--all" },
      { key: "unread", label: "Unread", value: unreadCount, className: "notifications-summary-card--unread" },
      { key: "read", label: "Read", value: readCount, className: "notifications-summary-card--read" },
    ],
    [totalCount, unreadCount, readCount]
  );

  return (
    <div className="notifications-center-page container-fluid px-3 px-lg-4">
      <div className="notifications-center-header d-flex flex-wrap justify-content-between align-items-start gap-3">
        <div>
          <h2>Notifications</h2>
          <p>
            {guard === "investor"
              ? "Stay on top of investments, orders, KYC, and property updates."
              : "Stay on top of cooperative activity, approvals, and updates."}
          </p>
        </div>
        <button
          type="button"
          className="btn btn-sm"
          style={{
            background: "var(--color-primary)",
            color: "var(--color-primary-text)",
            border: "none",
            borderRadius: "10px",
            padding: "8px 16px",
            fontWeight: 600,
          }}
          onClick={handleMarkAllAsRead}
          disabled={unreadCount <= 0}
        >
          Mark all as read
        </button>
      </div>

      <div className="notifications-summary-grid">
        {summaryCards.map((card) => (
          <button
            key={card.key}
            type="button"
            className={`notifications-summary-card ${card.className} ${
              activeTab === card.key ? "is-active" : ""
            }`.trim()}
            onClick={() => setTab(card.key)}
          >
            <div className="notifications-summary-card__label">{card.label}</div>
            <div className="notifications-summary-card__value">{card.value}</div>
          </button>
        ))}
      </div>

      <div className="notifications-page-tabs">
        {summaryCards.map((tab) => (
          <button
            key={tab.key}
            type="button"
            className={`notifications-page-tab ${activeTab === tab.key ? "is-active" : ""}`}
            onClick={() => setTab(tab.key)}
          >
            {tab.label}
            <span className="notification-tab-badge">{tab.value}</span>
          </button>
        ))}
      </div>

      <div className="d-flex flex-wrap align-items-center gap-2 mb-3" data-tour="investor-notif-filters">
        <label htmlFor="notification-category-filter" className="small text-muted mb-0">
          Category
        </label>
        <select
          id="notification-category-filter"
          className="form-select form-select-sm"
          style={{ maxWidth: 220, borderRadius: 10 }}
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value)}
          aria-label="Filter notifications by category"
        >
          {CATEGORY_FILTER_OPTIONS.map((opt) => (
            <option key={opt.value || "all"} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </div>

      {selectedNotifications.length > 0 && (
        <div className="notifications-bulk-bar">
          <span className="small fw-semibold">
            {selectedNotifications.length} selected
          </span>
          <button
            type="button"
            className="notifications-action-btn"
            onClick={handleBulkMarkAsRead}
          >
            Mark as read
          </button>
          <button
            type="button"
            className="notifications-action-btn notifications-action-btn--danger"
            onClick={() => setBulkDeleteConfirmModal({ isOpen: true })}
          >
            Delete selected
          </button>
        </div>
      )}

      <div className="card border-0 shadow-sm rounded-4" data-tour="investor-notif-list">
        <div className="card-body p-3 p-lg-4">
          <div className="notifications-toolbar">
            <label className="d-flex align-items-center gap-2 mb-0 small text-muted">
              <input
                type="checkbox"
                checked={
                  notifications.length > 0 &&
                  notifications.every((n) => selectedNotifications.includes(n.id))
                }
                onChange={toggleSelectAllOnPage}
                disabled={notifications.length === 0}
              />
              Select all on page
            </label>
          </div>

          {isLoading || isFetching ? (
            <TableSkeletonLoader rows={6} columns={1} />
          ) : notifications.length > 0 ? (
            notifications.map((notification) => (
              <div key={notification.id} className="notifications-list-item">
                <input
                  type="checkbox"
                  className="notifications-list-item__checkbox"
                  checked={selectedNotifications.includes(notification.id)}
                  onChange={() => toggleSelect(notification.id)}
                  aria-label={`Select notification ${notification.title}`}
                />
                <div className="notifications-list-item__card">
                  <NotificationCard
                    notification={notification}
                    onClick={handleNotificationClick}
                  />
                </div>
                <div className="notifications-list-item__actions">
                  <button
                    type="button"
                    className="notifications-action-btn"
                    onClick={() => handleNotificationClick(notification)}
                  >
                    View
                  </button>
                  <button
                    type="button"
                    className="notifications-action-btn notifications-action-btn--danger"
                    onClick={() =>
                      setDeleteConfirmModal({ isOpen: true, notificationId: notification.id })
                    }
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))
          ) : (
            <div className="notification-empty py-5">
              <div className="notification-empty__icon" aria-hidden>
                <FiBell size={28} />
              </div>
              <p className="mb-0 fw-semibold">
                {activeTab === "unread"
                  ? "You're all caught up."
                  : activeTab === "read"
                    ? "No read notifications"
                    : "You're all caught up."}
              </p>
              <p className="small text-muted mt-1 mb-0">
                {guard === "investor"
                  ? "Updates about your investments, orders, and requests will show up here."
                  : "When something happens in your cooperative, it will show up here."}
              </p>
            </div>
          )}

          {pagination.total > 0 && pagination.last_page > 1 && (
            <div className="mt-4">
              <TablePagination
                currentPage={pagination.current_page || pageNumber}
                perPage={pagination.per_page || 20}
                total={pagination.total || 0}
                totalPages={pagination.last_page || 1}
                count={notifications.length}
                handlePageChange={handlePageChange}
              />
            </div>
          )}
        </div>
      </div>

      <ConfirmationModal
        isOpen={deleteConfirmModal.isOpen}
        onClose={() => setDeleteConfirmModal({ isOpen: false, notificationId: null })}
        onConfirm={confirmDeleteNotification}
        title="Delete notification"
        message="Are you sure you want to delete this notification? This action cannot be undone."
        confirmText="Yes, delete"
        cancelText="Cancel"
        confirmButtonClass="bg-danger text-white"
        cancelButtonClass="bg-secondary text-white"
        isLoading={isDeleting}
      />

      <ConfirmationModal
        isOpen={bulkDeleteConfirmModal.isOpen}
        onClose={() => setBulkDeleteConfirmModal({ isOpen: false })}
        onConfirm={confirmBulkDelete}
        title="Delete notifications"
        message={`Delete ${selectedNotifications.length} selected notification${
          selectedNotifications.length > 1 ? "s" : ""
        }? This cannot be undone.`}
        confirmText="Yes, delete"
        cancelText="Cancel"
        confirmButtonClass="bg-danger text-white"
        cancelButtonClass="bg-secondary text-white"
        isLoading={isDeleting}
      />
    </div>
  );
}
